import { createSlice } from "@reduxjs/toolkit";
import { SUPPORT_PARENT_PORTAL_KEY, SUPPORT_PARENT_SESSION_ENDED_KEY } from "../functions/parentImpersonation";

let logoutTimer;
let storeRef = null;

// Function to set the store reference (called after store is created)
export const setStoreRef = (store) => {
    storeRef = store;
};

// Helper function to calculate remaining time
const calculateRemainingTime = (expirationTime) => {
    const currentTime = new Date().getTime();
    const adjExpirationTime = new Date(expirationTime).getTime();
    return adjExpirationTime - currentTime;
};

// Helper function to check if the token is valid
const isTokenValid = (token, expirationTime) => {
    if (!token || !expirationTime) {
        return false; // Token or expiration time is missing
    }
    const remainingTime = calculateRemainingTime(expirationTime);
    return remainingTime > 0; // Token is valid if remaining time is greater than 0
};

// Helper function to set multiple localStorage items conditionally
const setLocalStorageItems = (items) => {
    Object.keys(items).forEach((key) => {
        if (items[key] !== undefined && items[key] !== null) {
            localStorage.setItem(key, items[key]);
        }
    });
};

// Helper function to clear multiple localStorage items
const clearLocalStorageItems = (keys) => {
    keys.forEach((key) => {
        localStorage.removeItem(key);
    });
};

const token = localStorage.getItem("token");
const expirationTime = localStorage.getItem("expirationTime");
const isValid = isTokenValid(token, expirationTime);
const isImpersonatingStored = isValid && localStorage.getItem("isImpersonating") === "true";

// Initial state
const initialState = {
    token: isValid ? token : "",
    isLoggedIn: isValid && localStorage.getItem("role") === "user",
    portalUserLoggedIn: isValid && localStorage.getItem("role") === "portalUser",
    hubUserLoggedIn: isValid && localStorage.getItem("role") === "hubUser",
    parentLoggedIn: isValid && localStorage.getItem("role") === "parent",
    donorLoggedIn: isValid && localStorage.getItem("role") === "donor",
    supportLoggedIn: isValid && localStorage.getItem("role") === "support",
    isImpersonating: isImpersonatingStored,
    supportIsAdmin: isValid && localStorage.getItem("supportIsAdmin") === "true",
    supportCanSelectCompany: isValid && localStorage.getItem("role") === "support" && localStorage.getItem("supportCanSelectCompany") !== "false",
    supportUserName: isValid ? localStorage.getItem("supportUserName") || "" : "",
    supportDisplayName: isValid ? localStorage.getItem("supportDisplayName") || "" : "",
    supportCompanyName: isValid ? localStorage.getItem("supportCompanyName") || "" : "",
    impersonatedUserName: isValid ? localStorage.getItem("impersonatedUserName") || "" : "",
    userName: isValid ? localStorage.getItem("sessionUserName") || "" : "",
    addPermission: isValid && localStorage.getItem("addPermission") === "true",
    updatePermission: isValid && localStorage.getItem("updatePermission") === "true",
    deletePermission: isValid && localStorage.getItem("deletePermission") === "true",
    hub: isValid && localStorage.getItem("hub") === "true",
    allowChargeCreditCard: isValid && localStorage.getItem("allowChargeCreditCard") === "true",
    url: process.env.REACT_APP_BACKEND_URL,
    permissions: {},
    permissionsLoaded: false,
    startupPage: undefined
};

const scheduleLogout = (expirationIso) => {
    const remainingTime = calculateRemainingTime(expirationIso);
    if (logoutTimer) clearTimeout(logoutTimer);
    logoutTimer = setTimeout(() => {
        if (storeRef) {
            storeRef.dispatch(authSlice.actions.logout());
        }
    }, remainingTime);
};

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        login(state, action) {
            const expirationTime = new Date(new Date().getTime() + action.payload.duration * 1000).toISOString();
            localStorage.removeItem(SUPPORT_PARENT_SESSION_ENDED_KEY);

            // Set localStorage items conditionally
            setLocalStorageItems({
                token: action.payload.token,
                expirationTime,
                addPermission: action.payload.addPermission?.toLowerCase() === "true",
                updatePermission: action.payload.updatePermission?.toLowerCase() === "true",
                deletePermission: action.payload.deletePermission?.toLowerCase() === "true",
                hub: action.payload.hub?.toLowerCase() === "true",
                allowChargeCreditCard: action.payload.allowChargeCreditCard?.toLowerCase() === "true",
                sessionUserName: action.payload.userName,
                sessionCompanyName: action.payload.companyName,
                inTrial: action.payload.inTrial,
                trialEndDate: action.payload.trialEndDate,
                role: action.payload.role,
                hasJiraCredentials: action.payload.hasJiraCredentials,
            });

            // Update state
            state.token = action.payload.token;
            state.isLoggedIn = action.payload.role === "user";
            state.portalUserLoggedIn = action.payload.role === "portalUser";
            state.hubUserLoggedIn = action.payload.role === "hubUser";
            state.parentLoggedIn = action.payload.role === "parent";
            state.donorLoggedIn = action.payload.role === "donor";
            state.supportLoggedIn = false;
            state.isImpersonating = false;
            state.userName = action.payload.userName;
            state.addPermission = action.payload.addPermission?.toLowerCase() === "true";
            state.updatePermission = action.payload.updatePermission?.toLowerCase() === "true";
            state.deletePermission = action.payload.deletePermission?.toLowerCase() === "true";
            state.hub = action.payload.hub?.toLowerCase() === "true";
            state.allowChargeCreditCard = action.payload.allowChargeCreditCard?.toLowerCase() === "true";
            state.startupPage = action.payload.startupPage?.trim() || '';

            scheduleLogout(expirationTime);
        },
        supportSession(state, action) {
            const expirationTime = new Date(new Date().getTime() + action.payload.duration * 1000).toISOString();
            const role = action.payload.role || "support";
            const isImpersonating = !!action.payload.isImpersonating;
            localStorage.removeItem(SUPPORT_PARENT_SESSION_ENDED_KEY);

            setLocalStorageItems({
                token: action.payload.token,
                expirationTime,
                role,
                isImpersonating: isImpersonating ? "true" : "false",
                supportIsAdmin: String(action.payload.isAdmin || "").toLowerCase() === "true" ? "true" : "false",
                supportCanSelectCompany: action.payload.canSelectCompany === true || String(action.payload.canSelectCompany || "").toLowerCase() === "true" ? "true" : "false",
                supportUserName: action.payload.supportUserName || "",
                supportDisplayName: action.payload.supportDisplayName || action.payload.supportUserName || "",
                supportCompanyName: action.payload.companyName || "",
                impersonatedUserName: action.payload.impersonatedUserName || "",
                sessionUserName: action.payload.userName || action.payload.impersonatedUserName || action.payload.supportUserName || "",
                sessionCompanyName: action.payload.companyName || "",
                addPermission: String(action.payload.addPermission || "false").toLowerCase() === "true",
                updatePermission: String(action.payload.updatePermission || "false").toLowerCase() === "true",
                deletePermission: String(action.payload.deletePermission || "false").toLowerCase() === "true",
                hub: String(action.payload.hub || "false").toLowerCase() === "true",
                allowChargeCreditCard: String(action.payload.allowChargeCreditCard || "false").toLowerCase() === "true",
                inTrial: action.payload.inTrial,
                trialEndDate: action.payload.trialEndDate,
                hasJiraCredentials: action.payload.hasJiraCredentials,
            });

            state.token = action.payload.token;
            state.supportLoggedIn = role === "support";
            state.isLoggedIn = role === "user";
            state.portalUserLoggedIn = false;
            state.hubUserLoggedIn = false;
            state.parentLoggedIn = role === "parent";
            state.donorLoggedIn = false;
            state.isImpersonating = isImpersonating;
            state.supportIsAdmin = String(action.payload.isAdmin || "").toLowerCase() === "true";
            state.supportCanSelectCompany = action.payload.canSelectCompany === true || String(action.payload.canSelectCompany || "").toLowerCase() === "true";
            state.supportUserName = action.payload.supportUserName || "";
            state.supportDisplayName = action.payload.supportDisplayName || action.payload.supportUserName || "";
            state.supportCompanyName = action.payload.companyName || "";
            state.impersonatedUserName = action.payload.impersonatedUserName || "";
            state.userName = action.payload.userName || action.payload.impersonatedUserName || action.payload.supportUserName || "";
            state.addPermission = String(action.payload.addPermission || "false").toLowerCase() === "true";
            state.updatePermission = String(action.payload.updatePermission || "false").toLowerCase() === "true";
            state.deletePermission = String(action.payload.deletePermission || "false").toLowerCase() === "true";
            state.hub = String(action.payload.hub || "false").toLowerCase() === "true";
            state.allowChargeCreditCard = String(action.payload.allowChargeCreditCard || "false").toLowerCase() === "true";
            state.startupPage = action.payload.startupPage?.trim?.() || state.startupPage || "";
            if (role === "support") {
                state.permissions = {};
                state.permissionsLoaded = true;
            }

            scheduleLogout(expirationTime);
        },
        clearImpersonation(state) {
            state.isImpersonating = false;
            state.impersonatedUserName = "";
            localStorage.setItem("isImpersonating", "false");
            localStorage.removeItem("impersonatedUserName");
        },
        logout(state) {
            // The parent pages redirect after this runs, when role/isImpersonating are already gone.
            const endedParentImpersonation = localStorage.getItem("role") === "parent"
                && localStorage.getItem("isImpersonating") === "true";

            // Clear localStorage items
            clearLocalStorageItems([
                "token",
                "expirationTime",
                "addPermission",
                "updatePermission",
                "deletePermission",
                "hub",
                "allowChargeCreditCard",
                "sessionUserName",
                "sessionCompanyName",
                "inTrial",
                "trialEndDate",
                "role",
                "hasJiraCredentials",
                "hubActiveTab", // Clear saved tab on logout
                "isImpersonating",
                "supportIsAdmin",
                "supportCanSelectCompany",
                "supportUserName",
                "supportDisplayName",
                "supportCompanyName",
                "impersonatedUserName",
                "supportSessionToken",
                "supportSessionCompany",
                "supportShowPicker",
                SUPPORT_PARENT_PORTAL_KEY,
            ]);
            if (endedParentImpersonation) localStorage.setItem(SUPPORT_PARENT_SESSION_ENDED_KEY, "true");

            if (logoutTimer) clearTimeout(logoutTimer);

            // Reset state properties
            state.token = "";
            state.isLoggedIn = false;
            state.portalUserLoggedIn = false;
            state.hubUserLoggedIn = false;
            state.parentLoggedIn = false;
            state.donorLoggedIn = false;
            state.supportLoggedIn = false;
            state.isImpersonating = false;
            state.supportIsAdmin = false;
            state.supportCanSelectCompany = false;
            state.supportUserName = "";
            state.supportDisplayName = "";
            state.supportCompanyName = "";
            state.impersonatedUserName = "";
            state.userName = "";
            state.addPermission = false;
            state.updatePermission = false;
            state.deletePermission = false;
            state.hub = false;
            state.allowChargeCreditCard = false;
            state.permissions = {};
            state.permissionsLoaded = false;
        },
        setPermissions(state, action) {
            state.permissions = action.payload;
            state.permissionsLoaded = true;
        },
    },
});

export const authActions = authSlice.actions;

export default authSlice;
